import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, ArrowLeftIcon, CogIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { api, apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';

const LocationSettingsPage = () => {
  const { user } = useAuth();
  const [currentLocation, setCurrentLocation] = useState({
    prefecture: user?.prefecture || '',
    city: user?.city || '',
    latitude: user?.latitude || null,
    longitude: user?.longitude || null,
    address: user?.location || ''
  });
  
  const [nearbyEmployers, setNearbyEmployers] = useState([]);
  const [mapApiKeys, setMapApiKeys] = useState(null);
  const [selectedMapType, setSelectedMapType] = useState('google'); // 'google' or 'mapkit'
  const [loading, setLoading] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  
  useEffect(() => {
    fetchMapApiKeys();
  }, []);
  
  useEffect(() => {
    if (currentLocation.latitude && currentLocation.longitude) {
      fetchNearbyEmployers();
      if (mapApiKeys) {
        initializeMap();
      }
    }
  }, [currentLocation.latitude, currentLocation.longitude, mapApiKeys, selectedMapType]);

  const fetchMapApiKeys = async () => {
    try {
      const response = await api.get('/api-keys/maps');
      if (response.status === 200) {
        setMapApiKeys(response.data);
      }
    } catch (error) {
      console.error('Error fetching map API keys:', error);
      toast.error('マップAPIキーの取得に失敗しました');
    }
  };

  const fetchNearbyEmployers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await api.get('/map/nearby-employers', {
        params: {
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          radius: 5000 // 5km
        }
      });
      if (response.status === 200) {
        setNearbyEmployers(response.data);
      }
    } catch (error) {
      console.error('Error fetching nearby employers:', error);
      toast.error('近くの事業者の取得に失敗しました');
    }
  };

  const getCurrentLocation = () => {
    setLoading(true);
    
    if (!navigator.geolocation) {
      toast.error('このブラウザでは位置情報を取得できません');
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        
        try {
          // 逆ジオコーディングで住所を取得
          const response = await api.get('/location/reverse-geocode', {
            params: {
              latitude: lat,
              longitude: lon
            }
          });
          if (response.status === 200) {
            const newLocation = {
              latitude: lat,
              longitude: lon,
              prefecture: response.data.location_name.split(/[都道府県]/)[0] + (response.data.location_name.includes('都') ? '都' : response.data.location_name.includes('道') ? '道' : response.data.location_name.includes('府') ? '府' : '県'),
              city: response.data.location_name.split(/[都道府県]/)[1] || '',
              address: response.data.location_name
            };
            
            setCurrentLocation(newLocation);
            toast.success('位置情報を更新しました');
          }
        } catch (error) {
          console.error('Error reverse geocoding:', error);
          setCurrentLocation(prev => ({
            ...prev,
            latitude: lat,
            longitude: lon
          }));
          toast.success('位置情報を更新しました');
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        toast.error('位置情報の取得に失敗しました');
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  const saveLocationSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('ログインが必要です');
        return;
      }

      const authApi = apiWithAuth(token);
      await authApi.put('/user/location-settings', currentLocation);
      
      toast.success('位置情報設定を保存しました');
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('位置情報設定の保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    if (!currentLocation.latitude || !currentLocation.longitude || !mapApiKeys) return;
    
    setMapLoading(true);
    
    if (selectedMapType === 'google' && window.google) {
      initializeGoogleMap();
    } else if (selectedMapType === 'mapkit' && window.mapkit) {
      initializeMapKit();
    } else {
      // APIが読み込まれていない場合は読み込む
      loadMapAPI();
    }
  };

  const loadMapAPI = () => {
    if (selectedMapType === 'google') {
      if (!window.google) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${mapApiKeys.google_maps_api_key}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => initializeGoogleMap();
        document.head.appendChild(script);
      } else {
        initializeGoogleMap();
      }
    } else if (selectedMapType === 'mapkit') {
      if (!window.mapkit) {
        const script = document.createElement('script');
        script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          window.mapkit.init({
            authorizationCallback: function(done) {
              done(mapApiKeys.mapkit_js_api_key);
            }
          });
          initializeMapKit();
        };
        document.head.appendChild(script);
      } else {
        initializeMapKit();
      }
    }
  };

  const initializeGoogleMap = () => {
    if (!window.google || !mapRef.current) return;
    
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: currentLocation.latitude, lng: currentLocation.longitude },
      zoom: 14,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'on' }]
        }
      ]
    });

    // 現在地マーカー
    new window.google.maps.Marker({
      position: { lat: currentLocation.latitude, lng: currentLocation.longitude },
      map: map,
      title: '現在地',
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="8" fill="#3B82F6" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="3" fill="white"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(24, 24)
      }
    });

    // 事業者マーカー
    nearbyEmployers.forEach((employer, index) => {
      if (employer.latitude && employer.longitude) {
        const marker = new window.google.maps.Marker({
          position: { lat: employer.latitude, lng: employer.longitude },
          map: map,
          title: employer.business_name,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#EF4444"/>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(24, 24)
          }
        });

        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div class="p-2">
              <h3 class="font-bold text-sm">${employer.business_name}</h3>
              <p class="text-xs text-gray-600 mt-1">${employer.description}</p>
              <div class="mt-2">
                <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  ${employer.distance_km}km
                </span>
              </div>
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
      }
    });

    googleMapRef.current = map;
    setMapLoading(false);
  };

  const initializeMapKit = () => {
    if (!window.mapkit || !mapRef.current) return;
    
    const map = new window.mapkit.Map(mapRef.current, {
      center: new window.mapkit.Coordinate(currentLocation.latitude, currentLocation.longitude),
      span: new window.mapkit.CoordinateSpan(0.01, 0.01)
    });

    // 現在地アノテーション
    const currentLocationAnnotation = new window.mapkit.MarkerAnnotation(
      new window.mapkit.Coordinate(currentLocation.latitude, currentLocation.longitude),
      {
        title: '現在地',
        color: '#3B82F6'
      }
    );
    map.addAnnotation(currentLocationAnnotation);

    // 事業者アノテーション
    nearbyEmployers.forEach(employer => {
      if (employer.latitude && employer.longitude) {
        const annotation = new window.mapkit.MarkerAnnotation(
          new window.mapkit.Coordinate(employer.latitude, employer.longitude),
          {
            title: employer.business_name,
            subtitle: `${employer.distance_km}km`,
            color: '#EF4444'
          }
        );
        map.addAnnotation(annotation);
      }
    });

    setMapLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Link
                to="/"
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                戻る
              </Link>
              <h1 className="text-xl font-bold text-gray-900 flex items-center">
                <MapPinIcon className="w-6 h-6 mr-2 text-blue-600" />
                位置情報設定
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 現在の位置情報 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">現在の位置情報</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">都道府県</label>
              <input
                type="text"
                value={currentLocation.prefecture}
                onChange={(e) => setCurrentLocation(prev => ({ ...prev, prefecture: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="東京都"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">市区町村</label>
              <input
                type="text"
                value={currentLocation.city}
                onChange={(e) => setCurrentLocation(prev => ({ ...prev, city: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="渋谷区"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">住所</label>
            <input
              type="text"
              value={currentLocation.address}
              onChange={(e) => setCurrentLocation(prev => ({ ...prev, address: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="詳細な住所を入力してください"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={getCurrentLocation}
              disabled={loading}
              className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <MapPinIcon className="w-4 h-4 mr-2" />
              )}
              現在地を取得
            </button>
            
            <button
              onClick={saveLocationSettings}
              disabled={loading}
              className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CogIcon className="w-4 h-4 mr-2" />
              設定を保存
            </button>
          </div>
        </div>

        {/* マップ表示選択 */}
        {mapApiKeys && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">マップ表示</h2>
            
            <div className="flex mb-4 space-x-4">
              <button
                onClick={() => setSelectedMapType('google')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  selectedMapType === 'google'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Google Maps
              </button>
              <button
                onClick={() => setSelectedMapType('mapkit')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  selectedMapType === 'mapkit'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Apple MapKit
              </button>
            </div>

            {/* マップ表示エリア */}
            <div className="relative h-96 bg-gray-200 rounded-lg overflow-hidden">
              {mapLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-gray-600">マップを読み込み中...</p>
                  </div>
                </div>
              )}
              <div ref={mapRef} className="w-full h-full"></div>
            </div>
          </div>
        )}

        {/* 近くの事業者一覧 */}
        {nearbyEmployers.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              近くの事業者 ({nearbyEmployers.length}件)
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {nearbyEmployers.map((employer) => (
                <div key={employer.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{employer.business_name}</h3>
                    <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded">
                      {employer.distance_km}km
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {employer.description}
                  </p>
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPinIcon className="w-4 h-4 mr-1" />
                    {employer.prefecture}{employer.city} {employer.address}
                  </div>
                  
                  {employer.categories && employer.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {employer.categories.slice(0, 3).map((category, index) => (
                        <span
                          key={index}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSettingsPage;